import React, { useEffect, useState } from 'react';
import { CreditCard, AlertCircle, TrendingUp, CheckCircle, Clock, Loader2, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import BookingModal from '../../components/BookingModal';

export default function Overview() {
  const { profile, user, updateProfile } = useAuth();
  const [stats, setStats] = useState({ total: 0, offers: 0, interviews: 0 });
  const [recentApps, setRecentApps] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [currency, setCurrency] = useState('NGN');
  const [hasUploadedCV, setHasUploadedCV] = useState(false);

  const isExpired = profile?.subscription_expiry && new Date() > new Date(profile.subscription_expiry);
  const hasPaid = profile?.subscription_status === 'active' && !isExpired;

  const daysLeft = (() => {
    if (!profile?.subscription_expiry) return 0;
    const msLeft = new Date(profile.subscription_expiry).getTime() - new Date().getTime();
    return Math.ceil(msLeft / (24 * 60 * 60 * 1000));
  })();

  const isExpiringSoon = profile?.subscription_status === 'active' && !isExpired && daysLeft > 0 && daysLeft <= 3;

  useEffect(() => {
    if (!user || !profile) return;

    const checkSubscriptionEmails = async () => {
      // 1. Warning Email (expires in <= 3 days)
      if (
        profile.subscription_status === 'active' &&
        !isExpired &&
        isExpiringSoon &&
        !profile.expiry_warning_sent
      ) {
        try {
          // Update profile local and remote state flags first to prevent duplicate sends
          await updateProfile({ expiry_warning_sent: true });
          
          await supabase.functions.invoke('send-email', {
            body: {
              user_id: user.id,
              subject: 'Subscription Expiring Soon! ⚠️',
              html: `
                <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; background-color: #ffffff;">
                  <h2 style="color: #D97706; margin-top: 0; font-size: 20px;">Subscription Expiring Soon ⚠️</h2>
                  <p>Hello ${profile.first_name || 'there'},</p>
                  <p>Your subscription plan at <strong>Placement Point</strong> is expiring in <strong>${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}</strong> (on ${new Date(profile.subscription_expiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}).</p>
                  <p>To avoid any disruption to your active applications and counseling sessions, please renew your plan as soon as possible.</p>
                  <div style="margin: 24px 0 12px 0;">
                    <a href="${window.location.origin}/dashboard/billing" style="display: inline-block; background-color: #D97706; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; font-weight: bold;">Renew Plan Now</a>
                  </div>
                  <p style="margin-top: 24px; font-size: 12px; color: #64748b; border-t: 1px solid #f1f5f9; padding-top: 12px;">If you have already renewed, please ignore this email.</p>
                </div>
              `
            }
          });
        } catch (err) {
          console.error('Error sending expiry warning email:', err);
        }
      }

      // 2. Expired Email
      if (
        isExpired &&
        profile.subscription_status === 'active' &&
        !profile.expiry_email_sent
      ) {
        try {
          // Update profile state to expired and mark notification as sent
          await updateProfile({ 
            expiry_email_sent: true,
            subscription_status: 'expired'
          });

          await supabase.functions.invoke('send-email', {
            body: {
              user_id: user.id,
              subject: 'Subscription Expired ❌',
              html: `
                <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; background-color: #ffffff;">
                  <h2 style="color: #E11D48; margin-top: 0; font-size: 20px;">Subscription Expired ❌</h2>
                  <p>Hello ${profile.first_name || 'there'},</p>
                  <p>Your subscription plan at <strong>Placement Point</strong> expired on <strong>${new Date(profile.subscription_expiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>.</p>
                  <p>As a result, your job applications processing has been put on hold. Please renew your subscription to resume our services immediately.</p>
                  <div style="margin: 24px 0 12px 0;">
                    <a href="${window.location.origin}/dashboard/billing" style="display: inline-block; background-color: #E11D48; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; font-weight: bold;">Renew Subscription</a>
                  </div>
                  <p style="margin-top: 24px; font-size: 12px; color: #64748b; border-t: 1px solid #f1f5f9; padding-top: 12px;">Placement Point Team</p>
                </div>
              `
            }
          });
        } catch (err) {
          console.error('Error sending subscription expired email:', err);
        }
      }
    };

    checkSubscriptionEmails();
  }, [profile, user, isExpired, isExpiringSoon, daysLeft]);

  useEffect(() => {
    if (!user) return;

    const fetchStats = async () => {
      try {
        const { data, error } = await supabase
          .from('applications')
          .select('*')
          .eq('user_id', user.id)
          .order('applied_at', { ascending: false });

        if (error) throw error;

        const apps = data || [];
        const activeApps = apps.filter(a => a.status === 'Interview' || a.status === 'Offered');
        
        setStats({
          offers: apps.filter(a => a.status === 'Offered').length,
          interviews: activeApps.length,
        });

        setRecentApps(activeApps.slice(0, 3));

        // Fetch user's documents to see if they uploaded any CV
        const { data: docData } = await supabase
          .from('documents')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);

        setHasUploadedCV(docData && docData.length > 0);
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, [user]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'Interview': return 'bg-purple-100 text-purple-700';
      case 'Offered': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const firstName = (profile?.first_name && profile.first_name !== 'null' && profile.first_name !== 'Unknown') 
    ? profile.first_name 
    : (user?.email ? user.email.split('@')[0].charAt(0).toUpperCase() + user.email.split('@')[0].slice(1) : 'there');

  const formatPlanName = (plan) => {
    if (!plan || plan === 'none' || plan === 'placement_fee') return 'Free';
    return plan.charAt(0).toUpperCase() + plan.slice(1).replace(/_/g, ' ');
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Welcome back, {firstName}!</h1>
        <p className="mt-1 text-slate-500">Here is what's happening with your job search today.</p>
      </div>

      {isExpiringSoon && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-md animate-pulse">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 text-amber-700 rounded-xl shrink-0">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-amber-950 text-sm">Action Required: Subscription Expiring Soon!</h4>
              <p className="text-xs text-amber-800 font-medium mt-0.5">
                Your {formatPlanName(profile?.subscription_plan)} subscription is expiring in <span className="font-bold text-amber-950">{daysLeft} {daysLeft === 1 ? 'day' : 'days'}</span> (on {new Date(profile.subscription_expiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}). Renew now to keep your applications running without interruption!
              </p>
            </div>
          </div>
          <Link 
            to="/dashboard/billing" 
            className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition-all shadow-md text-center whitespace-nowrap"
          >
            Renew Now
          </Link>
        </div>
      )}

      {profile?.assigned_email && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-100 text-indigo-700 rounded-xl">
              <ShieldCheck className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <h4 className="font-bold text-indigo-950 text-sm">Dedicated Job Application Account Active</h4>
              <p className="text-xs text-indigo-700 font-medium">We are applying to jobs on your behalf using <span className="font-bold text-indigo-900">{profile.assigned_email}</span></p>
            </div>
          </div>
          <Link 
            to="/dashboard/profile" 
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-indigo-100 text-center"
          >
            View Secure Credentials
          </Link>
        </div>
      )}


      {!hasPaid && isExpired && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6 shadow-sm">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0 text-rose-600 animate-pulse">
            <AlertCircle className="w-8 h-8" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-xl font-bold text-rose-950 mb-1">Subscription Expired!</h3>
            <p className="text-rose-800 text-sm mb-4 sm:mb-0">
              Your {formatPlanName(profile?.subscription_plan)} plan expired on <strong>{new Date(profile.subscription_expiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>. Renew your plan now to resume strategic job applications and counselling sessions!
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to="/dashboard/billing" 
              className="whitespace-nowrap px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-rose-200 text-center"
            >
              Renew Plan
            </Link>
          </div>
        </div>
      )}

      {!hasPaid && !isExpired && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6 shadow-sm">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-8 h-8 text-indigo-600" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-xl font-bold text-slate-900 mb-1">Activate Your Subscription</h3>
            <p className="text-slate-600 mb-4 sm:mb-0">
              You need an active subscription for us to start optimizing your CV and sending applications.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to="/dashboard/calendar"
              className="whitespace-nowrap px-6 py-3 bg-white text-indigo-600 border border-indigo-200 rounded-xl font-bold hover:bg-indigo-50 transition-colors shadow-sm"
            >
              Book Consultation
            </Link>
            <button 
              onClick={() => setShowPayment(true)}
              className="whitespace-nowrap px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
            >
              View Plans & Pay
            </button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Offers Received</p>
              <p className="text-2xl font-bold text-slate-900">
                {loadingStats ? <Loader2 className="w-5 h-5 animate-spin" /> : stats.offers}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-green-50 text-green-600 rounded-xl">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Interviews Secured</p>
              <p className="text-2xl font-bold text-slate-900">
                {loadingStats ? <Loader2 className="w-5 h-5 animate-spin" /> : stats.interviews}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Next Steps */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Next Steps</h3>
          <ul className="space-y-4">
            <li className="flex gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm ${hasPaid ? 'bg-green-100 text-green-700' : 'bg-indigo-600 text-white'}`}>
                {hasPaid ? '✓' : '1'}
              </div>
              <div>
                <p className="font-semibold text-slate-900">{hasPaid ? 'Strategy Session' : 'Book Free Consultation'}</p>
                <p className="text-sm text-slate-500">{hasPaid ? 'Book your included strategy session.' : 'Talk to us before making a payment.'}</p>
                <Link to="/dashboard/calendar" className="text-sm font-medium text-indigo-600 hover:text-indigo-500 inline-flex items-center mt-1">
                  Go to Calendar &rarr;
                </Link>
              </div>
            </li>
            <li className="flex gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm ${hasPaid ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                {hasPaid ? '✓' : '2'}
              </div>
              <div>
                <p className="font-semibold text-slate-900">{hasPaid ? 'Payment Complete' : 'Complete Payment'}</p>
                <p className="text-sm text-slate-500">{hasPaid ? 'Your subscription is active.' : 'Select a plan to start your journey.'}</p>
              </div>
            </li>
            <li className={`flex gap-4 ${!hasPaid ? 'opacity-50' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm ${
                hasUploadedCV
                  ? 'bg-green-100 text-green-700'
                  : 'bg-slate-200 text-slate-600'
              }`}>
                {hasUploadedCV ? '✓' : '3'}
              </div>
              <div>
                <p className="font-semibold text-slate-900">Upload your CV</p>
                <p className="text-sm text-slate-500 mb-1">Upload your current CV for us to review and optimize.</p>
                {hasPaid && (
                  <Link to="/dashboard/documents" className="text-sm font-medium text-indigo-600 hover:text-indigo-500 inline-flex items-center">
                    Go to Documents &rarr;
                  </Link>
                )}
              </div>
            </li>
            <li className={`flex gap-4 ${!hasPaid ? 'opacity-50' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm ${
                profile?.assigned_email
                  ? 'bg-green-100 text-green-700'
                  : 'bg-slate-200 text-slate-600'
              }`}>
                {profile?.assigned_email ? '✓' : '4'}
              </div>
              <div>
                <p className="font-semibold text-slate-900">Dedicated Gmail Account</p>
                <p className="text-sm text-slate-500">{profile?.assigned_email ? `Ready: ${profile.assigned_email}` : 'We will assign a dedicated email for your applications.'}</p>
              </div>
            </li>
          </ul>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Recent Updates</h3>
            <Link to="/dashboard/jobs" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">View all</Link>
          </div>
          {loadingStats ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : recentApps.length > 0 ? (
            <ul className="space-y-3">
              {recentApps.map((app) => (
                <li key={app.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{app.role}</p>
                    <p className="text-xs text-slate-500">{app.company} · {new Date(app.applied_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${getStatusColor(app.status)}`}>
                    {app.status}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3">
                <AlertCircle className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">No updates yet</p>
              <p className="text-sm text-slate-400 mt-1 max-w-xs">When you secure an interview or offer, it will appear here.</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPayment && (
          <BookingModal
            isOpen={showPayment}
            onClose={() => setShowPayment(false)}
            currency={currency}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
